import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ZorroTableComponent } from '../../../../components/common/custom-table/zorro-table.component';
import { NoDataComponent } from '../../../../components/common/no-data/no-data.component';
import { RepresentationModalComponent } from './representation-modal/representation-modal.component';

/** Normalised row that the template always consumes, regardless of tab type. */
export interface HotspotSuite {
  name: string;
  items: { component: string; test_case: any; reason?: string[]; solutions?: string[] }[];
  tableRows: any[];
}

@Component({
  selector: 'app-hotspot-summary',
  standalone: true,
  imports: [CommonModule, NoDataComponent, ZorroTableComponent],
  templateUrl: './hotspot-summary.component.html',
  styleUrl: './hotspot-summary.component.scss'
})
export class HotspotSummaryComponent implements OnInit {
  @Input() data: any[] = [];

  @Input() metricDisplayName: string = '';

  activeTab: any;

  /** Keys of currently-expanded accordion rows. */
  private readonly expandedNodes = new Set<string>();

  private readonly modalService = inject(NgbModal);

  /** Column definitions — recomputed per tab in buildDisplayItems(). */
  currentColumns: any[] = [];

  /** Pagination / display options for the table. */
  readonly tableOptions = {
    pageSize: 100000000,
    showPagination: false,
  };

  /** Cached list of suites for the active tab — updated only on tab switch. */
  displayItems: HotspotSuite[] = [];

  ngOnInit(): void {
    if (this.data?.length) {
      this.activeTab = this.data[0];
      this.buildDisplayItems();
    }
  }

  switchTab(tab: any): void {
    this.activeTab = tab;
    this.expandedNodes.clear();
    this.buildDisplayItems();
  }

  /**
   * Normalises the active tab's children into a unified structure and
   * pre-computes each suite's flat table rows so no method is called from
   * the template (which would create new array refs on every CD cycle).
   */
  private buildDisplayItems(): void {
    const tabName: string = this.activeTab?.name ?? '';
    const children: any[] = this.activeTab?.children ?? [];

    this.currentColumns = this.buildColumnsForTab(tabName);

    if (!children.length) {
      this.displayItems = [];
      return;
    }

    const toBullets = (arr?: string[]): string =>
      arr?.length ? arr.map(s => `• ${s}`).join('\n\n') : '—';

    const normalizeTestCase = (val: any): string => {
      if (Array.isArray(val)) return val.map(s => `• ${s}`).join('\n');
      if (typeof val === 'string') return val.replaceAll(String.raw`\n`, '\n');
      return val ?? '—';
    };

    const toTableRows = (items: any[]): any[] =>
      items.map(item => ({
        ...item,
        test_case_text: normalizeTestCase(item.test_case),
        reason_text: toBullets(item.reason),
        solutions_text: toBullets(item.solutions),
        representation_label: item.representation?.length ? `${item.representation.length} Records` : '—',
      }));

    const isSuiteLevel = children[0]?.children !== undefined;
    if (isSuiteLevel) {
      this.displayItems = children.map((suite: any) => {
        const items = suite.children ?? [];
        return { name: suite.name, items, tableRows: toTableRows(items) };
      });
    } else {
      // Flaky – items are direct rows; group under one named suite
      this.displayItems = [{ name: tabName, items: children, tableRows: toTableRows(children) }];
    }
  }

  get isFlakyTab(): boolean {
    return this.activeTab?.name === 'Flaky';
  }

  /** Handle link-click actions emitted from the Flaky flat table. */
  onFlakyTableAction(event: any): void {
    if (event?.type === 'link' && event?.data?.column?.key === 'representation_label') {
      // Blur the trigger element BEFORE opening the modal.
      // NgbModal captures document.activeElement at open-time as the element to
      // restore focus to when the modal closes. If nothing is focused, Bootstrap
      // skips focus-restoration entirely, so the browser never calls
      // scrollIntoView() — which is the root cause of the tab panel disappearing.
      (document.activeElement as HTMLElement | null)?.blur();

      const representation = event.data.row?.representation ?? [];
      const modalRef = this.modalService.open(RepresentationModalComponent, {
        centered: true,
        size: 'xl',
      });
      modalRef.componentInstance.representationData = representation;
    }
  }

  private buildColumnsForTab(tabName: string): any[] {
    const hdr = (width?: string) => ({
      'font-size': '14px',
      'font-weight': 700,
      ...(width ? { width } : {}),
    });

    const listCell = {
      'white-space': 'pre-wrap',
      'vertical-align': 'top',
      'font-size': '14px',
      'font-weight': '400',
      'line-height': '26px',
    };

    const topCell = { 'vertical-align': 'top', 'font-size': '14px', 'font-weight': '700' };

    const componentCol = { display_name: 'Component', key: 'component', headerStyles: hdr('18%'), cellStyles: topCell };
    const testCaseCol  = { display_name: 'Test Case', key: 'test_case_text', headerStyles: hdr('22%'), cellStyles: listCell };
    const reasonCol    = { display_name: 'Reason',    key: 'reason_text',    headerStyles: hdr(),      cellStyles: listCell };
    const solutionsCol = { display_name: 'Solutions', key: 'solutions_text', headerStyles: hdr(),      cellStyles: listCell };

    if (tabName === 'Critical') {
      return [componentCol, testCaseCol, reasonCol, solutionsCol];
    }
    if (tabName === 'Stable') {
      return [componentCol, testCaseCol, reasonCol];
    }
    // Flaky tab
    const repCol = {
      display_name: 'Representation',
      key: 'representation_label',
      headerStyles: hdr('10%'),
      cellStyles: {'font-size': '14px', 'font-weight': '700' },
      _config: { type: 'link' },
    };
    return [componentCol, testCaseCol, repCol];
  }

  toggleNode(name: string): void {
    if (this.expandedNodes.has(name)) {
      this.expandedNodes.delete(name);
    } else {
      this.expandedNodes.add(name);
    }
  }

  isExpanded(name: string): boolean {
    return this.expandedNodes.has(name);
  }

}

